import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Spinner,
  Center,
  useToast,
  VStack,
  HStack,
  useDisclosure,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  DownloadIcon,
  AddIcon,
  InfoIcon,
  QuestionIcon,
} from "@chakra-ui/icons";
import styled from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import { rootUrl } from "../../api/rootUrl";
import { UploadContactsModal } from "./modals/UploadContactsModal";
import { ReachChart } from "./charts/ReachChart";
import { PhoneTypeBreakdownChart } from "./charts/PhoneTypeBreakdownChart";
import {
  getContactTimeline,
  ContactTimelineData,
} from "../../api/clientUser/getContactTimeline";

const Container = styled.div`
  max-width: 1200px;
`;

const StyledStatBox = styled(Box)`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(51, 156, 255, 0.15);
    border-color: #339cff;
  }
`;

interface PhoneTypeBreakdown {
  mobile: number;
  landline: number;
  fixedVoip: number;
  nonFixedVoip: number;
  personal: number;
  tollFree: number;
  premium: number;
  voicemail: number;
  pager: number;
  unknown: number;
  noPhone: number;
}

interface SmsReachability {
  smsCapable: number;
  nonSmsCapable: number;
  noPhone: number;
}

interface ContactCompleteness {
  phoneAndEmail: number;
  phoneOnly: number;
  emailOnly: number;
  neither: number;
}

interface MultiplePhones {
  total: number;
  withWorkPhone: number;
  withHomePhone: number;
}

interface ContactAnalytics {
  totalContacts: number;
  optedOut: number;
  optedIn: number;
  reachable: number;
  withEmail: number;
  withPhone: number;
  qualityScore: number;
  phoneTypeBreakdown: PhoneTypeBreakdown;
  smsReachability: SmsReachability;
  contactCompleteness: ContactCompleteness;
  multiplePhones: MultiplePhones;
}

export const Contacts = () => {
  const { accessToken } = useAuth();
  const toast = useToast();
  const {
    isOpen: isUploadOpen,
    onOpen: onUploadOpen,
    onClose: onUploadClose,
  } = useDisclosure();
  const [analytics, setAnalytics] = useState<ContactAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [timelineData, setTimelineData] = useState<ContactTimelineData[]>([]);
  const [contactTimeFrame, setContactTimeFrame] = useState<
    "day" | "month" | "year"
  >("day");
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactTimeFrame]);

  const loadTimeline = async () => {
    if (!accessToken) return;

    setIsLoadingTimeline(true);
    try {
      const data = await getContactTimeline(accessToken, contactTimeFrame);
      setTimelineData(data.timeline);
    } catch (error) {
      console.error("Failed to load contact timeline:", error);
      toast({
        title: "Failed to load timeline",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  const loadAnalytics = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${rootUrl}/client/contact-analytics`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load analytics");

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to load contact analytics:", error);
      toast({
        title: "Failed to load analytics",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (!accessToken) return;

    setIsExporting(true);
    try {
      const response = await fetch(`${rootUrl}/client/export-contacts`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to export contacts");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Contacts exported successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to export contacts:", error);
      toast({
        title: "Failed to export contacts",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadSuccess = () => {
    loadAnalytics();
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="#339CFF" />
      </Center>
    );
  }

  if (!analytics) {
    return (
      <Container>
        <Heading size="lg" mb={4}>
          Contacts
        </Heading>
        <Box>No analytics available</Box>
      </Container>
    );
  }

  return (
    <Container>
      <UploadContactsModal
        isOpen={isUploadOpen}
        onClose={onUploadClose}
        onSuccess={handleUploadSuccess}
      />

      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="lg" color="#1A202C">
              Contact Analytics
            </Heading>
            <HStack spacing={1} mt={1}>
              <InfoIcon boxSize={3} color="blue.400" />
              <Text fontSize="xs" color="gray.500">
                Data refreshes every 5 minutes
              </Text>
            </HStack>
          </Box>
          <HStack spacing={3}>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="green"
              onClick={onUploadOpen}
              isDisabled={true}
            >
              Upload CSV
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              onClick={handleExport}
              isLoading={isExporting}
              loadingText="Exporting..."
            >
              Export CSV
            </Button>
          </HStack>
        </HStack>

        {/* Top Row - Campaign Reach */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <StyledStatBox>
            <Stat>
              <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                Total Contacts
              </StatLabel>
              <StatNumber color="#339CFF" fontSize="3xl" fontWeight="700">
                {analytics.totalContacts.toLocaleString()}
              </StatNumber>
              <StatHelpText color="gray.500">
                All contacts in your database
              </StatHelpText>
            </Stat>
          </StyledStatBox>

          <StyledStatBox>
            <Stat>
              <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                SMS Capable
              </StatLabel>
              <StatNumber color="#10B981" fontSize="3xl" fontWeight="700">
                {analytics.smsReachability?.smsCapable.toLocaleString() || 0}
              </StatNumber>
              <StatHelpText color="gray.500">
                Can receive text campaigns
              </StatHelpText>
            </Stat>
          </StyledStatBox>

          <StyledStatBox>
            <Stat>
              <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                With Email
              </StatLabel>
              <StatNumber color="#F59E0B" fontSize="3xl" fontWeight="700">
                {analytics.withEmail.toLocaleString()}
              </StatNumber>
              <StatHelpText color="gray.500">
                Can receive email campaigns
              </StatHelpText>
            </Stat>
          </StyledStatBox>
        </SimpleGrid>

        {/* Bottom Row - Data Health */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <StyledStatBox>
            <Stat>
              <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                Complete Contacts
              </StatLabel>
              <StatNumber color="#8B5CF6" fontSize="3xl" fontWeight="700">
                {analytics.contactCompleteness?.phoneAndEmail.toLocaleString() ||
                  0}
              </StatNumber>
              <StatHelpText color="gray.500">
                Have both phone and email
              </StatHelpText>
            </Stat>
          </StyledStatBox>

          <StyledStatBox>
            <Stat>
              <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                Opted Out
              </StatLabel>
              <StatNumber color="#EF4444" fontSize="3xl" fontWeight="700">
                {analytics.optedOut.toLocaleString()}
              </StatNumber>
              <StatHelpText color="gray.500">
                {analytics.totalContacts > 0
                  ? `${Math.round(
                      (analytics.optedOut / analytics.totalContacts) * 100
                    )}% of total`
                  : "0% of total"}
              </StatHelpText>
            </Stat>
          </StyledStatBox>

          <StyledStatBox>
            <Stat>
              <HStack spacing={2} mb={2}>
                <StatLabel color="gray.600" fontSize="sm" fontWeight="600">
                  Data Quality Score
                </StatLabel>
                <Tooltip
                  label="Score based on: SMS-capable contacts (60%), validated phone types (25%), and complete records (15%)"
                  fontSize="sm"
                  hasArrow
                  placement="top"
                  bg="gray.700"
                  color="white"
                  p={3}
                  borderRadius="md"
                >
                  <QuestionIcon boxSize={3} color="gray.400" cursor="help" />
                </Tooltip>
              </HStack>
              <StatNumber
                color={
                  analytics.qualityScore >= 80
                    ? "#10B981"
                    : analytics.qualityScore >= 60
                    ? "#F59E0B"
                    : "#EF4444"
                }
                fontSize="3xl"
                fontWeight="700"
              >
                {analytics.qualityScore}%
              </StatNumber>
              <StatHelpText color="gray.500">
                {analytics.qualityScore >= 80
                  ? "Excellent data quality"
                  : analytics.qualityScore >= 60
                  ? "Good data quality"
                  : "Needs improvement"}
              </StatHelpText>
            </Stat>
          </StyledStatBox>
        </SimpleGrid>

        {/* Phone Type Intelligence */}
        {analytics.phoneTypeBreakdown && (
          <PhoneTypeBreakdownChart
            phoneTypeBreakdown={analytics.phoneTypeBreakdown}
            smsReachability={analytics.smsReachability}
            contactCompleteness={analytics.contactCompleteness}
          />
        )}

        {/* Contact Timeline Chart */}
        {isLoadingTimeline ? (
          <Center py={8}>
            <Spinner size="lg" color="#339CFF" />
          </Center>
        ) : (
          <ReachChart
            data={timelineData}
            timeFrame={contactTimeFrame}
            onTimeFrameChange={setContactTimeFrame}
          />
        )}
      </VStack>
    </Container>
  );
};
